import type { Command, PropState } from "../document/commands.ts";
import type { JsonValue, MapDocument, PropBehaviour, PropInstance, TileRef } from "../document/types.ts";
import { changePropCommand, keepPropPlacementCommand, movePropCommand, removeEntityCommand, removePropCommand, setPropBehaviourCommand } from "./panel-commands.ts";
import type { PickOptions } from "./pick.ts";
import type { FrameSizeOf } from "./prop-hit.ts";
import type { PropRef } from "./templates.ts";

/**
 * Many props at once (P1.6-T33, ADR-070's amendment "many props at once"): what a marquee takes, the props
 * placing one frame or carrying one type, what a selection's props share and where they differ, and the
 * commands that act on many props or markers as one undo step. Every builder reads the map it is given and
 * returns plain commands; a builder with nothing to change returns null, one change its plain command and
 * more a `compound`, so one undo takes the whole edit back.
 */

/**
 * A marker as the selection holds it.
 */
export interface MarkerRef {
  readonly layerId: string;
  readonly entityId: string;
}

/**
 * A rectangle in world pixels between two corners, in either order.
 */
export interface WorldRect {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

/**
 * The rectangle with its edges ordered, so a marquee dragged up or left reads as one dragged down and right.
 *
 * @param rect The rectangle.
 * @returns Its edges, left at most right and top at most bottom.
 */
function ordered(rect: WorldRect): WorldRect {
  return { left: Math.min(rect.left, rect.right), top: Math.min(rect.top, rect.bottom), right: Math.max(rect.left, rect.right), bottom: Math.max(rect.top, rect.bottom) };
}

/**
 * The props a marquee takes: each on a usable prop layer whose art, the frame the map shows it in as drawn
 * from its anchor, lies wholly inside the rectangle, in layer order and each layer's order. A rectangle with
 * no area takes nothing.
 *
 * @param map The map.
 * @param usable Whether a layer may be selected from (visible and unlocked).
 * @param sizeOf Frame size lookup.
 * @param tileSize Tile size in pixels.
 * @param rect The marquee in world pixels.
 * @param options The frame each prop is shown with and which props may be taken, as a click sees them.
 * @returns The props.
 */
export function propsInRect(map: MapDocument, usable: (layerId: string) => boolean, sizeOf: FrameSizeOf, tileSize: number, rect: WorldRect, options: PickOptions = {}): PropRef[] {
  const box = ordered(rect);
  if (box.right <= box.left || box.bottom <= box.top) {
    return [];
  }
  const taken: PropRef[] = [];
  for (const layer of map.layers) {
    if (layer.kind !== "prop" || !usable(layer.id)) {
      continue;
    }
    for (const prop of layer.props) {
      if (options.pickable !== undefined && !options.pickable(layer.id, prop)) {
        continue;
      }
      const frame = options.frameOf?.(layer.id, prop) ?? prop.frame;
      const size = sizeOf(frame.tileset, frame.tile);
      const left = prop.x * tileSize;
      const bottom = (prop.y + 1) * tileSize;
      if (left >= box.left && left + size.w <= box.right && bottom - size.h >= box.top && bottom <= box.bottom) {
        taken.push({ layerId: layer.id, propId: prop.id });
      }
    }
  }
  return taken;
}

/**
 * The markers a marquee takes: each on a usable marker layer whose point lies inside the rectangle.
 *
 * @param map The map.
 * @param usable Whether a layer may be selected from (visible and unlocked).
 * @param tileSize Tile size in pixels.
 * @param rect The marquee in world pixels.
 * @returns The markers, in layer order.
 */
export function markersInRect(map: MapDocument, usable: (layerId: string) => boolean, tileSize: number, rect: WorldRect): MarkerRef[] {
  const box = ordered(rect);
  const taken: MarkerRef[] = [];
  for (const layer of map.layers) {
    if (layer.kind !== "entity" || !usable(layer.id)) {
      continue;
    }
    for (const entity of layer.entities) {
      const x = entity.x * tileSize;
      const y = entity.y * tileSize;
      if (x >= box.left && x <= box.right && y >= box.top && y <= box.bottom) {
        taken.push({ layerId: layer.id, entityId: entity.id });
      }
    }
  }
  return taken;
}

/**
 * The props on usable prop layers a test keeps, in layer order.
 *
 * @param map The map.
 * @param usable Whether a layer may be selected from.
 * @param keep The test.
 * @returns The props.
 */
function propsWhere(map: MapDocument, usable: (layerId: string) => boolean, keep: (prop: PropInstance) => boolean): PropRef[] {
  return map.layers.flatMap((layer) => (layer.kind !== "prop" || !usable(layer.id) ? [] : layer.props.filter(keep).map((prop) => ({ layerId: layer.id, propId: prop.id }))));
}

/**
 * Every prop placing a frame, mirrored or not (Select every prop of this frame).
 *
 * @param map The map.
 * @param usable Whether a layer may be selected from (visible and unlocked).
 * @param frame The frame.
 * @returns The props, in layer order.
 */
export function propsPlacing(map: MapDocument, usable: (layerId: string) => boolean, frame: TileRef): PropRef[] {
  return propsWhere(map, usable, (prop) => prop.frame.tileset === frame.tileset && prop.frame.tile === frame.tile);
}

/**
 * Every prop carrying a behaviour type (Select every prop of this type).
 *
 * @param map The map.
 * @param usable Whether a layer may be selected from (visible and unlocked).
 * @param type The type's id.
 * @returns The props, in layer order.
 */
export function propsCarrying(map: MapDocument, usable: (layerId: string) => boolean, type: string): PropRef[] {
  return propsWhere(map, usable, (prop) => prop.behaviour?.type === type);
}

/**
 * The frame every prop places, a mirror aside.
 *
 * @param props The props.
 * @returns The frame, or null when they place more than one or there are none.
 */
export function sharedFrame(props: readonly PropInstance[]): TileRef | null {
  const first = props[0];
  if (first === undefined || props.some((prop) => prop.frame.tileset !== first.frame.tileset || prop.frame.tile !== first.frame.tile)) {
    return null;
  }
  return { tileset: first.frame.tileset, tile: first.frame.tile };
}

/**
 * The behaviour type every prop carries.
 *
 * @param props The props.
 * @returns The type's id, or null when one carries none, they differ or there are none.
 */
export function sharedType(props: readonly PropInstance[]): string | null {
  const type = props[0]?.behaviour?.type;
  return type === undefined || props.some((prop) => prop.behaviour?.type !== type) ? null : type;
}

/**
 * What many field records share: each field whose value is the same in every record (an absent field is a
 * value too), and the fields whose values differ, which the inspector reads as Mixed.
 *
 * @param records The records.
 * @returns The shared values and the mixed fields' names, in the order first met.
 */
export function sharedFields(records: readonly { readonly [key: string]: JsonValue }[]): { readonly values: { readonly [key: string]: JsonValue }; readonly mixed: readonly string[] } {
  const names = [...new Set(records.flatMap((record) => Object.keys(record)))];
  const values: Record<string, JsonValue> = {};
  const mixed: string[] = [];
  for (const name of names) {
    const seen = new Set(records.map((record) => JSON.stringify(record[name] ?? null)));
    const value = records[0]?.[name];
    if (seen.size > 1) {
      mixed.push(name);
    } else if (value !== undefined) {
      values[name] = value;
    }
  }
  return { values, mixed };
}

/**
 * The mirror a mirror of many props sets: on for them all, unless every one is mirrored on that axis already,
 * when off for them all.
 *
 * @param props The props.
 * @param axis The axis.
 * @returns The mirror to set.
 */
export function mirrorTarget(props: readonly PropInstance[], axis: "x" | "y"): boolean {
  return !props.every((prop) => (axis === "x" ? prop.flipX : prop.flipY));
}

/**
 * Commands as one edit.
 *
 * @param commands The commands, in the order they run.
 * @returns Null for none, the command for one, a compound for more.
 */
export function together(commands: readonly Command[]): Command | null {
  if (commands.length === 0) {
    return null;
  }
  return commands.length === 1 ? (commands[0] as Command) : { kind: "compound", commands };
}

/**
 * The props of a selection the map holds, each with its layer.
 *
 * @param map The map.
 * @param refs The selection.
 * @returns The props found, in the selection's order.
 */
export function selectedProps(map: MapDocument, refs: readonly PropRef[]): { readonly layerId: string; readonly prop: PropInstance }[] {
  return refs.flatMap((ref) => {
    const layer = map.layers.find((l) => l.id === ref.layerId);
    const prop = layer?.kind === "prop" ? layer.props.find((p) => p.id === ref.propId) : undefined;
    return prop === undefined ? [] : [{ layerId: ref.layerId, prop }];
  });
}

/**
 * Removals of things held by index, per layer from the last index down, so each removal's recorded index
 * still names its thing when it runs and the reversed inverse puts each back where it was.
 *
 * @param map The map.
 * @param kind The layer kind the things sit on.
 * @param refs The things, by layer and id.
 * @param remove Builds one removal.
 * @returns The removals.
 */
function removals(map: MapDocument, kind: "prop" | "entity", refs: readonly { readonly layerId: string; readonly id: string }[], remove: (layerId: string, id: string) => Command): Command[] {
  const commands: Command[] = [];
  for (const layer of map.layers) {
    if (layer.kind !== kind) {
      continue;
    }
    const wanted = new Set(refs.filter((ref) => ref.layerId === layer.id).map((ref) => ref.id));
    const ids = layer.kind === "prop" ? layer.props.map((p) => p.id) : layer.kind === "entity" ? layer.entities.map((e) => e.id) : [];
    for (let index = ids.length - 1; index >= 0; index -= 1) {
      const id = ids[index] as string;
      if (wanted.has(id)) {
        commands.push(remove(layer.id, id));
      }
    }
  }
  return commands;
}

/**
 * Removes many props as one command.
 *
 * @param map The map.
 * @param refs The props.
 * @returns The command, or null when none of them is on the map.
 */
export function removePropsCommand(map: MapDocument, refs: readonly PropRef[]): Command | null {
  return together(
    removals(
      map,
      "prop",
      refs.map((ref) => ({ layerId: ref.layerId, id: ref.propId })),
      (layerId, id) => removePropCommand(map, layerId, id),
    ),
  );
}

/**
 * Removes many markers as one command.
 *
 * @param map The map.
 * @param refs The markers.
 * @returns The command, or null when none of them is on the map.
 */
export function removeMarkersCommand(map: MapDocument, refs: readonly MarkerRef[]): Command | null {
  return together(
    removals(
      map,
      "entity",
      refs.map((ref) => ({ layerId: ref.layerId, id: ref.entityId })),
      (layerId, id) => removeEntityCommand(map, layerId, id),
    ),
  );
}

/**
 * Moves many props as one command, each to where the function puts it; a prop left where it stands is left
 * out of the command.
 *
 * @param map The map.
 * @param refs The props.
 * @param to Where a prop goes, in tiles on the pixel lattice.
 * @returns The command, or null when nothing moves.
 */
export function movePropsCommand(map: MapDocument, refs: readonly PropRef[], to: (prop: PropInstance) => { readonly x: number; readonly y: number }): Command | null {
  return together(
    selectedProps(map, refs).flatMap(({ layerId, prop }) => {
      const at = to(prop);
      return at.x === prop.x && at.y === prop.y ? [] : [movePropCommand(map, layerId, prop.id, at.x, at.y)];
    }),
  );
}

/**
 * Changes many props' mirrors or sort offsets as one command, each by what the function gives it; a prop the
 * change leaves as it is is left out.
 *
 * @param map The map.
 * @param refs The props.
 * @param change The fields to change on a prop.
 * @returns The command, or null when nothing changes.
 */
export function changePropsCommand(map: MapDocument, refs: readonly PropRef[], change: (prop: PropInstance) => Partial<Pick<PropState, "flipX" | "flipY" | "sortPixels">>): Command | null {
  return together(
    selectedProps(map, refs).flatMap(({ layerId, prop }) => {
      const changes = change(prop);
      const same = (changes.flipX === undefined || changes.flipX === prop.flipX) && (changes.flipY === undefined || changes.flipY === prop.flipY) && (changes.sortPixels === undefined || changes.sortPixels === prop.sortPixels);
      return same ? [] : [changePropCommand(map, layerId, prop.id, changes)];
    }),
  );
}

/**
 * Keeps many props as placed, or stops keeping them (ADR-040 amendment of 2026-10-04, P1.6-T96), as one
 * command: the mark alone changes, no prop moves, and a prop already so is left out.
 *
 * @param map The map.
 * @param refs The props.
 * @param keep True to keep them as placed, false to stop.
 * @returns The command, or null when every prop is already so or none is there.
 */
export function keepPropsPlacementCommand(map: MapDocument, refs: readonly PropRef[], keep: boolean): Command | null {
  return together(refs.flatMap((ref) => keepPropPlacementCommand(map, ref.layerId, ref.propId, keep) ?? []));
}

/**
 * Gives many props behaviours as one command, each the one the function gives it (undefined takes it off);
 * a prop whose behaviour already equals it is left out.
 *
 * @param map The map.
 * @param refs The props.
 * @param behaviourOf The behaviour a prop takes.
 * @returns The command, or null when nothing changes.
 */
export function propsBehaviourCommand(map: MapDocument, refs: readonly PropRef[], behaviourOf: (prop: PropInstance) => PropBehaviour | undefined): Command | null {
  return together(
    selectedProps(map, refs).flatMap(({ layerId, prop }) => {
      const behaviour = behaviourOf(prop);
      return JSON.stringify(behaviour ?? null) === JSON.stringify(prop.behaviour ?? null) ? [] : [setPropBehaviourCommand(map, layerId, prop.id, behaviour)];
    }),
  );
}

/**
 * A selection with each prop found again by its id where it now stands: a prop moved to another layer (by
 * `movePropsToLayerCommand`, its undo or its redo) keeps its id, which is unique across a map's layers, so the
 * selection follows it. A prop no layer holds keeps its ref, for the caller to let go of.
 *
 * @param map The map.
 * @param refs The selection.
 * @returns The same array when no prop moved, else the refs rewritten.
 */
export function followMovedProps(map: MapDocument, refs: readonly PropRef[]): readonly PropRef[] {
  const holders = new Map<string, Set<string>>();
  const idsOf = (layerId: string): Set<string> | undefined => {
    let ids = holders.get(layerId);
    if (ids === undefined) {
      const layer = map.layers.find((l) => l.id === layerId);
      if (layer?.kind !== "prop") {
        return undefined;
      }
      ids = new Set(layer.props.map((p) => p.id));
      holders.set(layerId, ids);
    }
    return ids;
  };
  if (refs.every((ref) => idsOf(ref.layerId)?.has(ref.propId) === true)) {
    return refs;
  }
  for (const layer of map.layers) {
    idsOf(layer.id);
  }
  return refs.map((ref) => {
    if (holders.get(ref.layerId)?.has(ref.propId) === true) {
      return ref;
    }
    const found = [...holders].find(([, ids]) => ids.has(ref.propId));
    return found === undefined ? ref : { layerId: found[0], propId: ref.propId };
  });
}

/**
 * Moves many props to another prop layer as one command (P1.6-T51, ADR-070's amendment of 2026-09-27): each
 * leaves its layer, the removals running per layer from the last index down as `removePropsCommand`'s do, and
 * joins the end of the target's list with its id, place, mirrors, sort offset and behaviour, in the map's layer
 * order and each layer's own order, so the reversed inverse puts every prop back at its index. Props already on
 * the target are left out.
 *
 * @param map The map.
 * @param refs The props.
 * @param targetId The prop layer they move to.
 * @returns The command, or null when none of them moves.
 * @throws Error when the target is missing or holds no props.
 */
export function movePropsToLayerCommand(map: MapDocument, refs: readonly PropRef[], targetId: string): Command | null {
  const target = map.layers.find((l) => l.id === targetId);
  if (target === undefined || target.kind !== "prop") {
    throw new Error(`layer ${targetId} is not a prop layer of map ${map.id}`);
  }
  const moving = refs.filter((ref) => ref.layerId !== targetId);
  const removed = removals(
    map,
    "prop",
    moving.map((ref) => ({ layerId: ref.layerId, id: ref.propId })),
    (layerId, id) => removePropCommand(map, layerId, id),
  );
  const wanted = new Set(moving.map((ref) => `${ref.layerId}\u0000${ref.propId}`));
  const arriving = map.layers.flatMap((layer) => (layer.kind === "prop" && layer.id !== targetId ? layer.props.filter((prop) => wanted.has(`${layer.id}\u0000${prop.id}`)) : []));
  const added = arriving.map((prop, offset): Command => ({ kind: "addProp", mapId: map.id, layerId: targetId, index: target.props.length + offset, prop }));
  return added.length === 0 ? null : { kind: "compound", commands: [...removed, ...added] };
}
